import { z } from "zod";

export const GeocodingAccuracyEnum = z
  .enum(["rooftop", "interpolated", "centroid", "approximate", "failed"])
  .describe("Geocode Accuracy");

export const GeocodingCacheSchema = z.object({
  address_hash: z.string().describe("Address Hash"),
  latitude: z.number().describe("Latitude"),
  longitude: z.number().describe("Longitude"),
  geocode_accuracy: GeocodingAccuracyEnum.describe("Geocode Accuracy"),
  geocode_source: z.string().describe("Geocode Source"),
  geocoded_at: z.string().describe("Geocoded At"),
  is_validated: z.boolean().nullable().describe("Validated"),
  validation_source: z.string().nullable().describe("Validation Source"),
  validated_at: z.string().nullable().describe("Validated At"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
});
export type GeocodingCache = z.infer<typeof GeocodingCacheSchema>;

// -- Property Enrichment -----------------------------------------------------

export const PropertyEnrichmentProviderEnum = z
  .enum(["smarty", "realie", "batchdata"])
  .describe("Property Enrichment Provider");

// All fields are `.nullish()` (null | undefined | missing). Provider payloads
// vary in coverage — Smarty/Realie/BatchData each emit different subsets — and
// the curate pipeline only extracts a subset of the union. Requiring every key
// would reject partial payloads at IMPORT_ROW_SCHEMAS.location validation.
export const PropertyDataSchema = z.object({
  lot_sqft: z.number().nullish().describe("Lot Size (sqft)"),
  building_sqft: z.number().nullish().describe("Building Size (sqft)"),
  year_built: z.number().nullish().describe("Year Built"),
  bedrooms: z.number().nullish().describe("Bedrooms"),
  bathrooms: z.number().nullish().describe("Bathrooms"),
  stories: z.number().nullish().describe("Stories"),
  assessed_value: z.number().nullish().describe("Assessed Value"),
  market_value: z.number().nullish().describe("Market Value"),
  last_sale_price: z.number().nullish().describe("Last Sale Price"),
  last_sale_date: z.string().nullish().describe("Last Sale Date"),
  land_use_code: z.string().nullish().describe("Land Use Code"),
  land_use_description: z.string().nullish().describe("Land Use Description"),
  property_type: z.string().nullish().describe("Property Type"),
  construction_type: z.string().nullish().describe("Construction Type"),
  roof_type: z.string().nullish().describe("Roof Type"),
  heating_type: z.string().nullish().describe("Heating Type"),
  cooling_type: z.string().nullish().describe("Cooling Type"),
  tax_amount: z.number().nullish().describe("Tax Amount"),
  zoning: z.string().nullish().describe("Zoning"),
});
export type PropertyData = z.infer<typeof PropertyDataSchema>;

export const PropertyEnrichmentCacheSchema = z.object({
  address_hash: z.string().describe("Address Hash"),
  provider: PropertyEnrichmentProviderEnum.describe("Provider"),
  property_data: PropertyDataSchema.describe("Property Data"),
  enriched_at: z.string().describe("Enriched At"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
});
export type PropertyEnrichmentCache = z.infer<
  typeof PropertyEnrichmentCacheSchema
>;

// -- Visual Feature Extraction (ADV-738) ------------------------------------

// Closed enums per the locked spec. "unknown" is the fallback when Gemini
// cannot determine a value with confidence; null means the field was not
// included in the extraction (e.g. NAIP-only run has no exterior_material).

export const VisualFeaturesSchema = z.object({
  house_style: z
    .enum([
      "ranch",
      "colonial",
      "craftsman",
      "contemporary",
      "modern",
      "tudor",
      "victorian",
      "mediterranean",
      "cape_cod",
      "split_level",
      "bungalow",
      "other",
      "unknown",
    ])
    .nullish()
    .describe("House Style"),
  exterior_material: z
    .enum([
      "brick",
      "vinyl_siding",
      "wood_siding",
      "stucco",
      "fiber_cement",
      "stone",
      "other",
      "unknown",
    ])
    .nullish()
    .describe("Exterior Material"),
  exterior_color_hex: z.string().nullish().describe("Exterior Color Hex"),
  roof_type: z
    .enum([
      "asphalt_shingle",
      "metal",
      "tile",
      "flat",
      "wood_shake",
      "slate",
      "unknown",
    ])
    .nullish()
    .describe("Roof Type"),
  has_driveway: z.boolean().nullish().describe("Has Driveway"),
  driveway_material: z
    .enum(["concrete", "asphalt", "gravel", "paver", "none", "unknown"])
    .nullish()
    .describe("Driveway Material"),
  has_fence: z.boolean().nullish().describe("Has Fence"),
  fence_material: z
    .enum([
      "wood",
      "vinyl",
      "chain_link",
      "aluminum",
      "brick",
      "none",
      "unknown",
    ])
    .nullish()
    .describe("Fence Material"),
  lot_shape: z
    .enum(["corner", "interior", "cul-de-sac", "unknown"])
    .nullish()
    .describe("Lot Shape"),
  multi_story: z.boolean().nullish().describe("Multi Story"),
  has_pool: z.boolean().nullish().describe("Has Pool"),
  has_outbuilding: z.boolean().nullish().describe("Has Outbuilding"),
  vegetation_density: z
    .enum(["dense", "moderate", "sparse", "none"])
    .nullish()
    .describe("Vegetation Density"),
  neighborhood_type: z
    .enum(["suburban", "urban", "rural", "semi-rural"])
    .nullish()
    .describe("Neighborhood Type"),
  scene_description: z.string().nullish().describe("Scene Description"),
  mapillary_attribution: z.string().nullish().describe("Mapillary Attribution"),
  fallback_reason: z.string().nullish().describe("Fallback Reason"),
});
export type VisualFeatures = z.infer<typeof VisualFeaturesSchema>;

export const BusinessEnrichmentSettingsSchema = z.object({
  property_enrichment_enabled: z
    .boolean()
    .optional()
    .describe("Property Enrichment Enabled"),
  property_enrichment_provider:
    PropertyEnrichmentProviderEnum.optional().describe(
      "Property Enrichment Provider",
    ),
  property_enrichment_types: z
    .array(z.string())
    .optional()
    .describe("Enrichment Location Types"),
});
export type BusinessEnrichmentSettings = z.infer<
  typeof BusinessEnrichmentSettingsSchema
>;
